/**
 * DEV-1927: every color the Overview board uses, by its name in the SWAP DSM (Figma variables).
 * Konva shapes read it directly; Mantine components read the same object as `theme.other.dsm`.
 * Only the colors the cards use so far are here; a ticket that needs another one adds it, with its
 * DSM name, and never types the hex in a component.
 */
export const dsmTokens = {
  bg: {
    page: '#f2f3f4',
    surface: '#ffffff',
    /** DEV-1952 UAT: the teams "+N" of the 1.5 W and 2 W cards (Figma "bg/sidebar", Team tag kind More, Small and XSmall). */
    sidebar: '#f8f9fa',
    /** DEV-1931: the Overdue chip's fill (Figma "bg/warning") — distinct from orange/0 below. */
    warning: '#fffaed',
    /** DEV-1931: the hovercard's divider between title/chips and its facts (Figma "bg/selected"). */
    selected: '#e2efff',
  },
  text: {
    primary: '#2c2c2c',
    secondary: '#6d6e71',
    /** DEV-1950: the quick add card's placeholder and hint (Figma "text/muted"). */
    muted: '#9e9fa1',
    /** DEV-1950: the quick add card's date error (Figma "text/danger"). */
    danger: '#ef4444',
  },
  /** DEV-1950: the quick add card's drop shadow (Figma "Item / Quick add": 0 4 12.5, 8% black). */
  elevation: {
    popover: '0 4px 12.5px rgba(0, 0, 0, 0.08)',
  },
  /** DSM "border" (the default one for outlines, like the "+N more" chip). */
  border: {
    default: '#e2e3e4',
    /** DEV-1928: the Overview week and month lines and the lane-names column line (Figma "border/strong"). */
    strong: '#d1d2d3',
  },
  neutral: {
    2: '#6d6e71',
    3: '#9e9fa1',
    4: '#d1d2d3',
    /** DEV-1932: the hovercard container's outline (Figma "neutral/6"). */
    6: '#ebebec',
    7: '#f2f3f4',
    9: '#ffffff',
  },
  /** orange/4 is the selection ring (DEV-1929: the DSM variable is #F59125, not the #F6A246 typed by hand in 5 files). */
  orange: {
    0: '#fff2de',
    4: '#f59125',
    /** DEV-1931: the Overdue chip's text (Figma "orange/5"). */
    5: '#db770d',
  },
  red: {
    5: '#bf3636',
    /** DEV-1931: the Critical path chip's fill (Figma "red/0"). */
    0: '#fdecec',
  },
  pink: {
    3: '#ef5c9a',
  },
  secondary: {
    2: '#a0bade',
    /** DEV-1931: the ID span on a listed item row ("ECC-210"), and the owner team tag's outline. */
    4: '#4175be',
    /** DEV-1931: the milestone diamond's default fill (Phase/Milestone hovercards). */
    6: '#2a4c7c',
  },
  /** DEV-1931: hovercard chip/tag brand teal (Figma "primary/0", "primary/4", "primary/5"). */
  primary: {
    0: '#ecf8f7',
    4: '#41beb2',
    5: '#34988e',
    /** DEV-1928: the header corner's "Open all lanes" label (Figma "primary/6"). */
    6: '#2a7c74',
  },
  /** Card type colors (DSM action/*). */
  action: {
    /** DEV-1929: the default (task) type color, Figma "action/task". */
    task: '#ced4da',
    meeting: '#41beb2',
    clusterMeeting: '#f6a246',
  },
  workflow: {
    permitting: '#7c5cff',
  },
  /** Components/critical-path: the 2 px outline of a critical card, the same red as the code. */
  criticalPath: '#e03131',
  /** DEV-1931 UAT reopen 4: the 3-stop teal → blue → orange gradient every AI badge in the app uses
   *  (ProjectFiles.tsx's own `AI_BADGE_GRADIENT`, Figma node 3131:17494), as flat stops so a guarded
   *  file (hovercards, ui/overlay) can build the `linear-gradient(...)` string without a hex literal
   *  of its own — `teal` is the same value as `primary[4]`/`action.meeting`, named again here because
   *  Figma names it separately in this gradient. */
  ai: {
    teal: '#41beb2',
    blue: '#4693ff',
    orange: '#f6a246',
  },
} as const

export type DsmTokens = typeof dsmTokens
